<template>
  <div class="video-player">
    <div
      v-if="live"
      class="alert alert-info mb-3 text-center"
    >
      <div
        v-if="!isLive"
      >
        We're going live soon! Stay tuned.
      </div>
      <div
        v-if="isLive"
      >
        We're live! Video not playing?
        <a
          href="javascript:;"
          class="text-info"
          @click.prevent="reload"
        >
          Reload
        </a>
      </div>
    </div>
    <div
      v-show="currentTrackIsVideo"
      class="position-relative"
    >
      <video
        :id="id"
        :class="`video-js vjs-default-skin vjs-variant-${variant}`"
      >
      </video>
      <button
        v-if="isMuted && !isStaticImage"
        class="btn btn-dark btn-unmute"
        @click.prevent="unmute()"
      >
        <i class="fa fa-fw fa-volume-up mr-1"></i>
        Unmute
      </button>
    </div>
    <div
      v-show="currentTrackIsEmbed"
      class="position-relative"
    >
      <div class="embed-responsive embed-responsive-16by9">
        <iframe
          v-if="currentTrack && currentTrackIsEmbed"
          v-bind="embedProps"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.btn-unmute {
  position: absolute;
  top: 15px;
  right: 15px;

  @media screen and (max-width: 992px) {
    position: fixed;
    top: 85px;
    right: 10px;
  }
}

.vjs-variant-background {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  height: 99vh;
  max-width: inherit;
  display: flex;
  background-color: black;
  justify-content: center;
  align-items: center;
  padding: 0 !important;

  @media screen and (max-width: 600px) {
    border-top: 1px solid #222;
    top: 72px;
  }

  @media screen and (max-width: 992px) {
    bottom: auto;
    padding-top: 56.25% !important;
    height: 0 !important;
  }
}

.vjs-variant-business-plan {
  border: 1px solid #222;

  @media screen and (max-width: 992px) {
    position: fixed;
    top: 72px;
    left: 0;
    right: 0;
    border: none;
    bottom: auto;
    padding-top: 56.25% !important;
    height: 0 !important;
  }

  @media screen and (max-width: 600px) {
    border: none;
    border-top: 1px solid #222;
  }
}

.video-js {
  #static-image-wrapper {
    position: absolute;
    top: 0;
    left: 0;

    img {
      width: 100%;
    }
  }

  &.vjs-static-image  {
    .vjs-big-play-button {
      display: none !important;
    }
    .vjs-control-bar {
      display: none !important;
    }
    .btn-unmute {
      display: none !important;
    }
  }
}
</style>

<script>
import $ from 'jquery'
import { get, isArray, filter, merge } from 'lodash'
import videojs from 'video.js'
import 'videojs-playlist'
import 'videojs-youtube'
import '@/lib/videojs-image'
import '@/lib/videojs-wistia'
// doesn't work, no events or hooks: import '@/lib/videojs-facebook'

export default {
  props: {
    variant: {
      type: String,
      default: 'default'
    },
    thumbnail: {
      type: String,
      default: ''
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    controls: {
      type: Boolean,
      default: true
    },
    repeat: {
      type: Boolean,
      default: true
    },
    muted: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'video/mp4'
    },
    src: {
      type: String,
      default: ''
    },
    playlist: {
      type: [String, Array],
      default: null
    }
  },
  data () {
    return {
      live: false,
      isLive: false,
      player: null,
      tracks: [],
      isMuted: null,
      isStaticImage: false,
      initializing: true,
    }
  },
  computed: {
    id () {
      return 'player_' + this._uid
    },
    style () {
      let style = 'background-color:#e6e7e9;'
      if (this.thumbnail) {
        style += `background-image:url(${this.thumbnail});`
      }
      return style
    },
    currentTrack () {
      let track = {}
      if (this.player && this.tracks.length  && this.player.playlist) {
        track = this.tracks[this.player.playlist.currentItem()]
      }
      return track
    },
    currentTrackIsVideo () {
      return !this.currentTrackIsEmbed
    },
    currentTrackIsEmbed () {
      return this.currentTrack && this.currentTrack.embed
    },
    embedProps () {
      let props = {}
      if (this.currentTrack && this.currentTrackIsEmbed) {
        props = merge(
          this.currentTrack.embed_attributes,
          {
            class: "embed-responsive-item",
            src: this.currentTrack.sources[0].src,
          }
        )
      }
      return props
    }
  },
  watch: {
    tracks () {
      this.player.playlist(this.tracks)
      this.player.playlist.autoadvance(0)
      this.player.playlist.repeat(this.repeat)
      this.player.playlist.currentItem(0)
    },
    playlist () {
      if (isArray(this.playlist)) {
        this.tracks = this.playlist
        this.player.play()
      }
    }
  },
  async mounted() {
    if ('background' === this.variant) {
      $('#app').prepend($(this.$el))
    }

    let config = {
      controls: this.controls,
      fluid: true,
      autoplay: false,
      muted: this.muted,
      youtube: {
        playsinline: 1
      }
    }

    if (this.thumbnail) {
      config.poster = this.thumbnail
    }

    this.player = videojs(this.id, config, () => {

    })

    this.player.on('ready', () => {
      let currentTrack = this.player && this.player.playlist && this.player.playlist.currentItem()
      let track = this.playlist[currentTrack]
      this.isStaticImage = this.isStaticImageTrack(track)
    })

    this.player.on('play', (e) => {
      let currentTrack = this.player && this.player.playlist && this.player.playlist.currentItem()
      let track = this.playlist[currentTrack]
      this.isStaticImage = this.isStaticImageTrack(track)
      if (track && currentTrack.live) {
        this.muted(false)
      } else if (this.isMuted) {
        this.player.muted(true)
      }
      this.$emit('player.play', e, this.player)
    })

    this.player.on('pause', (e) => {
      this.$emit('player.pause', e, this.player)
    })

    this.player.on('volumechange', (e) => {
      if (this.player.muted()) {
        this.isMuted = true
        this.$emit('player.muted', e, this.player)
      } else {
        this.isMuted = false
        this.$emit('player.unmuted', e, this.player)
      }
    })

    this.player.on('playlistitem', (e, video) => {
      // do tracking here, e.g., markVideoViewed(video)
      this.$emit('player.playlistitem', e, this.player)
    })

    if (config.muted) {
      this.isMuted = true
      this.$emit('player.muted', null, this.player)
    }

    if (this.type && this.src) {
      this.tracks = [
        {
          sources: [
            {
              src: this.src,
              type: this.type
            }
          ]
        }
      ];
    } else if (isArray(this.playlist)) {
      this.tracks = this.playlist
    } else if (this.playlist !== null) {
      // TODO: load playlist from the cms videos endpoint
    }

    this.$emit('player.pause', null, this.player)

    if (this.autoplay) {
      this.player.play()
    }

    this.$nextTick(() => this.initializing = false);
  },
  beforeDestroy() {
    if (this.player) {
      this.player.dispose()
    }
  },
  methods: {
    pause () {
      this.player.pause()
    },
    play (track) {
      if (track !== undefined) {
        this.player.playlist.currentItem(track)
        this.player.play()
      } else {
        this.player.play()
      }
    },
    mute () {
      this.isMuted = true
      this.player.muted(true)
    },
    unmute () {
      this.isMuted = false
      this.player.muted(false)
    },
    reload () {
      document.location.reload()
    },
    isStaticImageTrack (track) {
      if (track) {
        for (let source of track.sources) {
          if (source.type.indexOf('image/') === 0) {
            return true
          }
        }
      }
      return false
    }
  }
}
</script>